import { Controller } from "@hotwired/stimulus"

// FD-168: a collapsible work-item group section. Starts collapsed (the
// Done/Cancelled state sections on the list view) with its body hidden — and,
// crucially, with no rows rendered server-side: the body holds only a "Load
// more" button. So a collapsed section costs one cheap COUNT and nothing else
// until opened.
//
// FD-194: rows are deferred behind a manual "Load more" button (FD-190 replaced
// the old auto-firing scroll sentinel). Since you expand a section precisely to
// see its rows, the first expand triggers that button once to fetch page one;
// any further pages are loaded by the button as usual.
//
// FD-269: now used on EVERY list group so each header chevron can collapse/expand.
// Eager groups render their rows server-side, so #loadDeferredRows is a no-op for
// them (no load-more sentinel to click). `openAdd` expands the group (if needed)
// and opens the inline "+ New work item" <details> at the foot of the group.
//
// FD-926: an optional `storageKey` remembers the choice in localStorage (the board's
// swimlane rows pass one per view + sub-group). A remembered choice beats the server's
// default on connect. Without a key nothing is read or written, so the group keeps its
// server-decided state. The try/catch mirrors board_column: a layout preference is worth
// nothing if private mode or a quota error breaks the page.
// FD-1189: the list groups pass one too (view + group-by + group); see #keep.
export default class extends Controller {
  static targets = ["body", "chevron", "trigger"]
  static values = { collapsed: Boolean, storageKey: String }

  initialize() {
    // FD-269: only groups that START collapsed defer their rows behind a "Load
    // more" sentinel. Eager groups render their rows server-side, so they must
    // NEVER auto-click their (manual) load-more on expand — mark them loaded so
    // #loadDeferredRows is a no-op for them. The initial collapsedValueChanged
    // fires AFTER initialize() (before connect), so this snapshot is the
    // authoritative "started collapsed" flag and beats that first callback.
    this.loaded = !this.collapsedValue
  }

  connect() {
    const stored = this.#read()
    if (stored !== null) this.collapsedValue = stored === "1"
  }

  toggle() {
    this.collapsedValue = !this.collapsedValue
    this.#write()
  }

  // FD-926: a broadcast refresh morphs this element back to the server markup — body
  // un-hidden, chevron rotated, collapsed-value reset — so a remembered collapse would
  // silently re-open. Mounted as `turbo:morph@document->collapsible#restore` by callers
  // that pass a storageKey. Renders explicitly: Stimulus compares the value attribute to
  // what it last saw, so resetting it to the stored value fires no changed callback while
  // the classes the morph rewrote stay wrong.
  restore() {
    const stored = this.#read()
    if (stored === null) return
    this.collapsedValue = stored === "1"
    this.#render()
  }

  // FD-1189: mounted as `turbo:before-morph-element->collapsible#keep`. Writes the
  // remembered choice into the INCOMING markup, so the morph never flips the group.
  // restore() runs after the morph, leaving an expanded Done group briefly collapsed; a
  // turbo:morph listener that reads layout first (FD-1188's) then clamps the scroll pane.
  // The event bubbles from every morphed descendant; only this element's counts.
  keep(event) {
    const incoming = event.detail?.newElement
    if (event.target !== this.element || !(incoming instanceof Element)) return
    const stored = this.#read()
    if (stored === null) return
    const collapsed = stored === "1"
    incoming.setAttribute(`data-${this.identifier}-collapsed-value`, String(collapsed))
    // Mirrors #render on the incoming tree, where Stimulus targets don't exist yet.
    this.#ownTarget(incoming, "body")?.classList.toggle("hidden", collapsed)
    this.#ownTarget(incoming, "chevron")?.classList.toggle("rotate-90", !collapsed)
    this.#ownTarget(incoming, "trigger")?.setAttribute("aria-expanded", String(!collapsed))
  }

  // FD-1189: root's own `name` target, skipping nested collapsibles (the board lane's columns).
  #ownTarget(root, name) {
    const scope = `[data-controller~="${this.identifier}"]`
    return Array.from(root.querySelectorAll(`[data-${this.identifier}-target~="${name}"]`))
      .find((el) => el.parentElement?.closest(scope) === root)
  }

  collapsedValueChanged() {
    this.#render()
    if (!this.collapsedValue) this.#loadDeferredRows()
  }

  #render() {
    if (this.hasBodyTarget) this.bodyTarget.classList.toggle("hidden", this.collapsedValue)
    // chevron points right when collapsed, rotates to point down when open.
    if (this.hasChevronTarget) this.chevronTarget.classList.toggle("rotate-90", !this.collapsedValue)
    // Optional: the button that drives this section, so screen readers hear the state
    // change and not just an unlabelled press. Guarded — callers that don't declare a
    // trigger (the work-item list/board groups) are unaffected.
    if (this.hasTriggerTarget) this.triggerTarget.setAttribute("aria-expanded", String(!this.collapsedValue))
  }

  // FD-269: the per-group "+" — ensure the group is expanded, then trigger the group's
  // "+ New work item" row. CLM-442: that row is now a button that opens the shared
  // create dialog carrying this group's prefills, so clicking it does the whole job
  // (modal_controller focuses the title field once the frame loads).
  openAdd(event) {
    event?.preventDefault()
    if (this.collapsedValue) {
      this.collapsedValue = false
      this.#write()
    }
    requestAnimationFrame(() => {
      const scope = this.hasBodyTarget ? this.bodyTarget : this.element
      scope.querySelector("[data-quick-add]")?.click()
    })
  }

  #read() {
    if (!this.storageKeyValue) return null
    try {
      return localStorage.getItem(this.storageKeyValue)
    } catch {
      return null
    }
  }

  #write() {
    if (!this.storageKeyValue) return
    try {
      localStorage.setItem(this.storageKeyValue, this.collapsedValue ? "1" : "0")
    } catch {
      // Remembering the layout is a nicety; never let a storage failure break the board.
    }
  }

  // Fetch the first page once, the first time the section is opened. After that
  // the rows (and any remaining "Load more" button) persist across collapse.
  #loadDeferredRows() {
    if (this.loaded || !this.hasBodyTarget) return
    const button = this.bodyTarget.querySelector("[data-load-more-url-value]")
    if (button) {
      this.loaded = true
      button.click()
    }
  }
}
